import { memo } from 'react'
import { ExternalLink, RotateCw } from 'lucide-react'
import { useStreamWatchdog } from '../../hooks/useStreamWatchdog.js'

/**
 * Transport du retour antenne. Le WebRTC est le défaut ; derrière un tunnel
 * Cloudflare il ne passe pas et on retombe sur HLS pour tout accès HTTPS —
 * même règle que la page /watch, dont ce panneau est la version embarquée.
 */
function previewUrl(): string {
  const params = 'controls=false&muted=true&autoplay=true&playsInline=true'
  const useHls = window.location.protocol === 'https:'
  return `/stream/${useHls ? 'hls' : 'webrtc'}/antenna/?${params}`
}

/**
 * Retour vidéo d'antenne, en tête du rail de la Grille.
 *
 * Lecture seule et coupé au son : c'est un contrôle visuel de ce qui sort,
 * pas un lecteur. Le son sur un poste de préparation partirait dans le studio.
 */
export const AntennaReturn = memo(function AntennaReturn() {
  // Le lecteur se relance seul quand le flux se fige : une coupure réseau du
  // poste ne doit plus obliger l'opérateur à recharger la Grille entière.
  const { playerKey, iframeRef, live, unavailable, onFrameLoad, reload } = useStreamWatchdog()

  return (
    <section className="monitor-return" aria-label="Retour antenne">
      <div className="monitor-return__header">
        <span className="tp-panel-title">Retour antenne</span>
        <span className={`monitor-return__state${live ? ' is-live' : ''}`} aria-live="polite">
          <span className="monitor-return__dot" aria-hidden="true" />
          {live ? 'LIVE' : unavailable ? 'INDISPONIBLE' : 'CONNEXION'}
        </span>
        <button
          type="button"
          className="monitor-return__action"
          onClick={reload}
          title="Recharger le retour vidéo"
          aria-label="Recharger le retour vidéo"
        >
          <RotateCw size={12} aria-hidden="true" />
        </button>
        <a
          href="/watch"
          target="_blank"
          rel="noreferrer"
          className="monitor-return__action"
          title="Ouvrir le retour antenne en plein écran"
          aria-label="Ouvrir le retour antenne en plein écran"
        >
          <ExternalLink size={12} aria-hidden="true" />
        </a>
      </div>

      <div className="monitor-return__frame">
        <iframe
          key={playerKey}
          ref={iframeRef}
          src={previewUrl()}
          title="Retour vidéo d’antenne"
          allow="autoplay"
          onLoad={onFrameLoad}
          className={`monitor-return__iframe${unavailable ? ' is-hidden' : ''}`}
        />
        {unavailable && <div className="monitor-return__unavailable">Retour non disponible</div>}
      </div>
    </section>
  )
})
